import React, { useState, useCallback, useEffect } from 'react';

import AttachmentButtons from 'components/AttachmentButtons';
import { useCommunityContext } from 'components/CommunityContext';
import MentionsTextArea from 'components/Mentions/MentionsTextArea';
import Modal from 'koba/components/Modal';
import ModalHeader from 'koba/components/Modal/ModalHeader';
import ModalBody from 'koba/components/Modal/ModalBody';
import Label from 'koba/components/Label';
import Icon from 'koba/components/Icon';
import { ButtonWithIconOnly } from 'koba/components/Button';
import { COMMENT_FORM_ID, SUCCESS } from 'utils/constants';
import { isTextDark } from 'utils/colors';
import classNames from 'classnames/bind';
import { TFunction } from 'next-i18next';
import { HandleMentionsChange as HandleMentionsChangeFuntionType } from 'components/Mentions/interfaces';
import { AttachmentInput as AttachmentType } from 'types/attachment';
import { Attachment } from 'schema/Post/types';

import styles from './AddCommentModal.module.scss';
import { isValidText } from '../../utils/textHelpers';

const cx = classNames.bind(styles);

interface RelevantMention {
  avatarUrl: string;
  display: string;
  id: string;
}

interface AddCommentModalProps {
  additionalClassName?: string;
  attachment?: Attachment;
  attachmentAltText?: string | null;
  cancelComment: () => void;
  color: string;
  handleAttachmentAltTextChange?: (altText: string) => void;
  handleAttachmentChange: (commentAttachment?: Attachment) => void;
  handleMentionsChange: HandleMentionsChangeFuntionType;
  isOpen?: boolean;
  isReply?: boolean;
  isSubmitDisabled?: boolean;
  postAttachment?: AttachmentType;
  relevantMentions?: RelevantMention[];
  renderButton?: (openCommentModal: () => void, value?: string) => void;
  submitComment: () => Promise<string | undefined>;
  t: TFunction;
  value: string;
}
export const AddCommentModal: React.FC<AddCommentModalProps> = ({
  additionalClassName = '',
  color,
  cancelComment,
  value,
  handleAttachmentChange,
  handleMentionsChange,
  isOpen = false,
  postAttachment = null,
  renderButton = null,
  submitComment,
  t,
  isReply = false,
  relevantMentions = [],
  attachment = null,
  attachmentAltText = '',
  handleAttachmentAltTextChange = () => null,
  isSubmitDisabled = true,
}) => {
  const textClassName = cx(
    isTextDark(color)
      ? 'post-comment__modal-header-text--dark'
      : 'post-comment__modal-header-text--light'
  );

  const [isEditing, setIsEditing] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(
    isSubmitDisabled
  );
  const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
  const { community } = useCommunityContext();

  useEffect(() => {
    setSubmitButtonDisabled(isSubmitDisabled);
  }, [isSubmitDisabled]);

  const openCommentModal = () => {
    document.body.style.overflow = 'hidden';
    setIsEditing(true);
    setSubmitButtonDisabled(false);
  };

  const closeCommentModal = () => {
    setIsEditing(false);
    document.body.style.overflow = 'visible';
    cancelComment();
  };

  const postComment = async () => {
    if (!isEditing) setSubmitButtonDisabled(true);
    const status = await submitComment();

    if (status === SUCCESS) {
      setIsEditing(false);
    }

    document.body.style.overflow = 'visible';
  };

  const onAttachmentUploading = useCallback(() => {
    setIsAttachmentUploading(true);
    if (!isEditing) setSubmitButtonDisabled(true);
  }, [attachment]);

  const onAttachmentChange = useCallback(
    (updatedAttachment: Attachment) => {
      handleAttachmentChange(updatedAttachment);
      setIsAttachmentUploading(false);
      if (!isEditing) setSubmitButtonDisabled(false);
    },
    [attachment]
  );

  const onAltTextChange = useCallback(
    (altText) => {
      handleAttachmentAltTextChange(altText);
      if (!isEditing) setSubmitButtonDisabled(false);
    },
    [attachment]
  );

  const buttonLabel = isReply ? t('Post reply') : t('Post comment');
  const mentionsEnabledPlaceholder = t(
    'Type @ to mention another member in your comment'
  );

  const postLabel = isReply
    ? t('components-addCommentModal-post_a_reply', 'Post a reply...')
    : mentionsEnabledPlaceholder;

  const disableSubmitButton =
    (!attachment && !isValidText(value)) ||
    submitButtonDisabled ||
    isAttachmentUploading;

  return (
    <>
      {renderButton && renderButton(openCommentModal, value)}

      <Modal
        closeModal={closeCommentModal}
        isOpen={isOpen || isEditing}
        modalBodyClasses={cx('post-comment__modal')}
        isFullWidth
      >
        {/* @ts-ignore */}
        <ModalHeader
          className={textClassName}
          data-qa="add-comment-modal-header"
          style={{ backgroundColor: color }}
        >
          {/* @ts-ignore */}
          <ButtonWithIconOnly
            appearance="knockout"
            ariaLabel={t('Close modal')}
            data-qa="close-modal"
            id="close-modal"
            onClick={closeCommentModal}
          >
            <Icon className={textClassName} name="x-dismiss" />
          </ButtonWithIconOnly>
          {isReply ? t('Reply') : t('Comment')}
          {/* @ts-ignore */}
          <ButtonWithIconOnly
            appearance="knockout"
            ariaLabel={buttonLabel}
            color={color}
            data-qa="post-comment"
            id="post-comment"
            isDisabled={disableSubmitButton}
            onClick={postComment}
          >
            <Icon className={textClassName} name="plane" />
          </ButtonWithIconOnly>
        </ModalHeader>
        <ModalBody
          className={cx('post-comment__modal-body', 'is-mentions-text-area')}
          hasTitle
          isFullWidth
        >
          <Label className={cx('sr-only')} htmlFor={COMMENT_FORM_ID}>
            {postLabel}
          </Label>
          <div className={cx('post-comment__attachment')}>
            <AttachmentButtons
              altText={attachmentAltText}
              attachment={attachment}
              isDisabled={!!postAttachment}
              s3UploadPath={community.s3UploadPath}
              isIconOnly
              onAltTextChange={onAltTextChange}
              onChange={onAttachmentChange}
              onUploading={onAttachmentUploading}
            />
          </div>

          <MentionsTextArea
            additionalClassName={additionalClassName}
            id="add-comment__modal"
            placeholder={postLabel}
            relevantMentions={relevantMentions}
            textAreaClassName="mentions__text-area"
            value={value}
            hasAutoFocus
            isModal
            onChange={handleMentionsChange}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default AddCommentModal;
